// journal/run — the Journal's interpreter: the module's ONE impure file.
// Performs each Effect against the JournalPort and the device's draft stash,
// and feeds the outcome back as a Msg.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { type JournalPorts, periodId } from "../../app/ports/journal.js";
import type { PortResult } from "../../app/ports/result.js";
import type { VersioningPorts } from "../../app/ports/versioning.js";
import type { Interpreter } from "../module.js";
import type { JournalEffect } from "./effect.js";
import type { JournalMsg } from "./msg.js";
import { periodAnchor } from "./view-model.js";

// Where an unsent own-text draft waits on this device, by period id.
export type DraftStash = {
    readonly read: (periodId: string) => string | null;
    readonly write: (periodId: string, text: string) => void;
    readonly remove: (periodId: string) => void;
};

export type JournalDeps = {
    readonly ports: JournalPorts & Pick<VersioningPorts, "loadEntityHistory">;
    readonly stash: DraftStash;
    // Scroll the element with this DOM id into view once it is rendered.
    readonly reveal: (anchor: string) => void;
};

export const mkRunJournal = ({
    ports,
    stash,
    reveal,
}: JournalDeps): Interpreter<JournalEffect, JournalMsg> => {
    const answer = async (
        call: PortResult<void>,
        ok: JournalMsg,
        failed: JournalMsg,
        dispatch: (msg: JournalMsg) => void,
    ): Promise<void> => dispatch(Result.isOk(await call) ? ok : failed);

    const load = async (
        effect: Extract<JournalEffect, { type: "load_page" }>,
        dispatch: (msg: JournalMsg) => void,
    ): Promise<void> => {
        const r = await ports.loadJournal(effect.scale, effect.from);
        if (Result.isErr(r)) {
            dispatch({ type: "page_failed", scale: effect.scale });
            return;
        }
        const stashed = new Map(
            r.value.periods.flatMap((p): [string, string][] => {
                const id = periodId(p.key);
                const text = stash.read(id);
                return text === null ? [] : [[id, text]];
            }),
        );
        dispatch({ type: "page_loaded", page: r.value, stashed });
    };

    return (effect, dispatch) => {
        switch (effect.type) {
            case "load_page":
                void load(effect, dispatch);
                return;
            case "reveal_period":
                reveal(periodAnchor(effect.periodId));
                return;
            case "stash_draft":
                stash.write(effect.periodId, effect.text);
                return;
            case "drop_stash":
                stash.remove(effect.periodId);
                return;
            case "write_own":
            case "erase_own": {
                const id = periodId(effect.key);
                const text = effect.type === "write_own" ? effect.text : "";
                void answer(
                    effect.type === "write_own"
                        ? ports.writeOwnText(effect.key, effect.text)
                        : ports.eraseOwnText(effect.key),
                    { type: "own_written", periodId: id, text },
                    { type: "own_write_failed", periodId: id, text },
                    dispatch,
                );
                return;
            }
            case "correct_assistant":
            case "unlock_assistant": {
                const id = periodId(effect.key);
                void answer(
                    effect.type === "correct_assistant"
                        ? ports.correctAssistantText(
                              effect.key,
                              effect.markdown,
                          )
                        : ports.unlockAssistantText(effect.key),
                    { type: "assistant_write_answered", periodId: id },
                    {
                        type: "assistant_write_failed",
                        periodId: id,
                        write:
                            effect.type === "correct_assistant"
                                ? "correct"
                                : "unlock",
                    },
                    dispatch,
                );
                return;
            }
            case "load_history": {
                const { resourceId } = effect;
                void ports.loadEntityHistory(resourceId).then((r) =>
                    dispatch(
                        Result.isOk(r)
                            ? {
                                  type: "history_loaded",
                                  resourceId,
                                  moments: r.value,
                              }
                            : { type: "history_failed", resourceId },
                    ),
                );
                return;
            }
            case "summarize": {
                const id = periodId(effect.key);
                void ports
                    .summarizePeriod(effect.key)
                    .then((r) =>
                        dispatch(
                            Result.isOk(r) && r.value.type === "summary_written"
                                ? { type: "summarized", periodId: id }
                                : { type: "summarize_failed", periodId: id },
                        ),
                    );
                return;
            }
            case "erase_all":
                void answer(
                    ports.eraseAllOwnTexts(),
                    { type: "all_erased" },
                    { type: "erase_all_failed" },
                    dispatch,
                );
                return;
            case "allow_reading":
                void answer(
                    ports.allowAssistantToReadOwn(),
                    { type: "consent_answered" },
                    { type: "consent_failed", before: false },
                    dispatch,
                );
                return;
            case "disallow_reading":
                void answer(
                    ports.disallowAssistantToReadOwn(),
                    { type: "consent_answered" },
                    { type: "consent_failed", before: true },
                    dispatch,
                );
                return;
            default:
                return exhaustSwitchCase(effect);
        }
    };
};
